import React, { createContext, FC, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useByoFilter from '@apiHooks/useByoFilter';
import useCustomRestaurantLists from '@apiHooks/useCustomRestaurantLists';
import { components } from '@api/api';
import { currentCountrySelector } from "@selectors/countries.selector";
import Loader from "@components/Loader/Loader";
import { RegionsEnum, SlicesEnum } from "@models/Byo";

export type MainFilterOutput = components['schemas']['MainFilterOutput'];
type RestaurantList = components['schemas']['MainOutputRestaurantList'];
export type CustomRestaurantList = components['schemas']['CustomRestaurantListViewModel'];
type CustomRestaurantListDetails = components['schemas']['CustomRestaurantListDetailsViewModel'];
export type RestaurantMenuContextType = {
    restaurantLists: RestaurantList[];
    rawFilterData: MainFilterOutput;
    tooltipsData: Array<{
        sliceId: SlicesEnum;
        id: number;
        title?: string;
        description?: string;
        imageUrl?: string;
    }>;
    customRestaurantLists: CustomRestaurantList[];
    isLoadingRestaurantLists: boolean;
    isLoadingCustomRestaurantLists: boolean;
    createCustomRestaurantList: (data: CustomRestaurantListDetails) => Promise<void>;
    updateCustomRestaurantList: (data: CustomRestaurantListDetails) => Promise<void>;
    deleteCustomRestaurantList: (countryCode: string, id: number) => Promise<void>;
}

export const defaultContextValue: RestaurantMenuContextType = {
    restaurantLists: [],
    rawFilterData: {},
    tooltipsData: [],
    customRestaurantLists: [],
    isLoadingRestaurantLists: true,
    isLoadingCustomRestaurantLists: true,
    createCustomRestaurantList: () => Promise.resolve(),
    updateCustomRestaurantList: () => Promise.resolve(),
    deleteCustomRestaurantList: () => Promise.resolve(),
};

export const RestaurantMenuContext = createContext<RestaurantMenuContextType>(defaultContextValue);

const RestaurantMenuProvider: FC = ({ children }) => {
    const countryCode = useSelector(currentCountrySelector);

    const {
        data: filterData,
        isLoading: isLoadingRestaurantLists,
    } = useByoFilter(countryCode);

    const {
        data: customRestaurantListsData,
        isLoading: isLoadingCustomRestaurantLists,
        createList,
        updateList,
        deleteList,
    } = useCustomRestaurantLists(countryCode);

    const restaurantLists = useMemo(
        () => (filterData?.restaurantLists ?? []).filter((item) => !item.isCustom) ?? [],
        [filterData?.restaurantLists]
    );

    const tooltipsData = useMemo(
        (): RestaurantMenuContextType['tooltipsData'] => [
            ...(filterData?.tooltips ?? []).map(i => ({
                sliceId: i.slice as SlicesEnum,
                id: i.id ?? 0,
                title: i.definition ?? '',
                description: `Examples: ${i.examples ?? ''}`,
            })),
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.West,
                imageUrl: '/West.svg',
            },
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.Midwest,
                imageUrl: '/Midwest.svg',
            },
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.South,
                imageUrl: '/South.svg',
            },
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.NorthEast,
                imageUrl: '/NorthEast.svg',
            },
        ],
        [filterData?.tooltips]
    );

    const customRestaurantLists = useMemo(
        () => customRestaurantListsData ?? [],
        [customRestaurantListsData]
    );

    const value = useMemo<RestaurantMenuContextType>(() => ({
        rawFilterData: filterData ?? {},
        restaurantLists,
        tooltipsData,
        customRestaurantLists,
        isLoadingRestaurantLists,
        isLoadingCustomRestaurantLists,
        createCustomRestaurantList: createList,
        updateCustomRestaurantList: updateList,
        deleteCustomRestaurantList: deleteList,
    }), [
        filterData,
        restaurantLists,
        tooltipsData,
        customRestaurantLists,
        isLoadingRestaurantLists,
        isLoadingCustomRestaurantLists,
        createList,
        updateList,
        deleteList,
    ]);

    if (isLoadingRestaurantLists) {
        return (
            <Loader
                isShown
                noBg
            />
        );
    }

    return (
        <RestaurantMenuContext.Provider value={value}>
            {children}
        </RestaurantMenuContext.Provider>
    );
};

export const useRestaurantMenu = () => {
    const context = useContext(RestaurantMenuContext);
    if (!context) {
        throw new Error('useRestaurantMenu must be used within RestaurantMenuProvider');
    }
    return context;
};

export default RestaurantMenuProvider;
